'use client'

import { TEAM_COLOR_OPTIONS } from '@ordo/constants/team-colors'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as React from 'react'

import { appIconStyle } from '../app-icon-style'
import { cn } from '../cn'
import { isCommittableHex, isInvalidHexDraft, resolveHexPairCommit, swatchHexValue } from './color-hex'
import { PhosphorIcon } from './icon-picker'
import { isBodyScrollLocked } from './popover'

/**
 * App branding uses the same named families and primary shades as Multipass
 * and the team picker. Custom background/foreground pairs remain supported.
 * Saved app branding is unchanged until an admin chooses and saves a pair.
 */
export interface ColorPairPreset {
  name: string
  bg: string
  fg: string
}

export const COLOR_PAIR_PRESETS: ColorPairPreset[] = TEAM_COLOR_OPTIONS.map((color) => ({
  name: color.name,
  bg: color.primary,
  fg: '#FFFFFF',
}))

export interface ColorPair {
  bg: string
  fg: string
}

export interface ColorPairPickerProps {
  bg: string
  fg: string
  onChange: (next: ColorPair) => void
  /** Phosphor icon name to render in the live preview tile. */
  previewIcon?: string | null
  /** Optional preview label rendered below the icon (e.g. app name). */
  previewLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Two-color picker for app branding. Renders a live preview tile so the
 * admin can verify legibility before saving. Internally a Radix Popover
 * exposes the curated palette + custom-hex inputs; the trigger itself
 * is the preview tile.
 *
 * Commit model for the hex text inputs: a strict '#RRGGBB' commits on every
 * keystroke, and blur / Enter / popover-close run a normalizing backstop
 * (adds a missing '#', expands 3-digit shorthand, uppercases). The close
 * backstop matters because the popover is non-modal and unmounts on
 * pointerdown-outside — a focused input removed from the DOM never fires
 * blur, which used to silently discard the typed value when the user
 * clicked the host form's Save button directly. Un-normalizable text shows
 * a visible invalid state instead of being silently dropped, and reverts
 * to the committed value when the popover closes.
 *
 * Invariant: every commit path fires at most ONE onChange per event. Hosts
 * commonly update via a render-closure setState (setDraft({ ...draft })),
 * where the second of two same-event calls would clobber the first.
 */
export function ColorPairPicker({
  bg,
  fg,
  onChange,
  previewIcon,
  previewLabel,
  disabled,
  className,
}: ColorPairPickerProps) {
  const [open, setOpen] = React.useState(false)
  const [bgDraft, setBgDraft] = React.useState(bg)
  const [fgDraft, setFgDraft] = React.useState(fg)

  React.useEffect(() => {
    setBgDraft(bg)
  }, [bg])
  React.useEffect(() => {
    setFgDraft(fg)
  }, [fg])

  const applyPreset = (preset: ColorPairPreset) => {
    onChange({ bg: preset.bg, fg: preset.fg })
  }

  const commitField = (key: 'bg' | 'fg', value: string) => {
    onChange({ bg: key === 'bg' ? value : bg, fg: key === 'fg' ? value : fg })
  }

  /**
   * Normalize-and-commit both drafts in one shot; garbage drafts revert to
   * the committed values. A case-only normalization (e.g. '#94a3b8' →
   * '#94A3B8') counts as a change — harmless, and it opportunistically
   * canonicalizes legacy lowercase values on save.
   */
  const commitDrafts = () => {
    const next = resolveHexPairCommit({ bg, fg }, { bg: bgDraft, fg: fgDraft })
    setBgDraft(next.bg)
    setFgDraft(next.fg)
    if (next.changed) {
      onChange({ bg: next.bg, fg: next.fg })
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      // Discard stale rejected text from a previous open/close cycle.
      setBgDraft(bg)
      setFgDraft(fg)
    } else {
      // Backstop for pointerdown-outside and Escape. Radix fires
      // onOpenChange(false) during pointerdown and React flushes discrete
      // events synchronously, so the host re-renders with the committed
      // colors before an outside Save button's click fires on pointerup.
      // Escape therefore commits normalizable pending drafts rather than
      // cancelling — consistent with presets committing on click.
      commitDrafts()
    }
    setOpen(nextOpen)
  }

  const handleHexKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitDrafts()
    }
  }

  const isPresetActive = (preset: ColorPairPreset) =>
    preset.bg.toLowerCase() === bg.toLowerCase() && preset.fg.toLowerCase() === fg.toLowerCase()

  const bgInvalid = isInvalidHexDraft(bgDraft)
  const fgInvalid = isInvalidHexDraft(fgDraft)

  // Note: the invalid ring can show transiently while typing through a 4-5
  // digit prefix; it is honest feedback and self-clears at 3 or 6 digits.
  const hexInputClass = (invalid: boolean) =>
    cn(
      'w-full rounded border border-input bg-background px-2 py-1 font-mono text-xs',
      invalid && 'border-destructive focus-visible:ring-destructive/40',
    )

  return (
    <PopoverPrimitive.Root modal={open && isBodyScrollLocked()} open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'group inline-flex flex-col items-center justify-center gap-1 rounded-md border border-input p-2 transition-colors',
            'hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
          aria-label={`Edit colors (background ${bg}, foreground ${fg})`}>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-md" style={appIconStyle(bg, fg)}>
            <PhosphorIcon
              name={previewIcon}
              className="h-6 w-6"
              fallback={
                <span className="text-xs font-semibold uppercase">{(previewLabel?.[0] ?? 'A').slice(0, 1)}</span>
              }
            />
          </span>
          {previewLabel ? <span className="text-[10px] text-muted-foreground">{previewLabel}</span> : null}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            'z-50 w-[22rem] max-w-[calc(100vw-16px)] rounded-md border bg-popover p-3 text-popover-foreground shadow-md outline-none',
            'max-h-[var(--radix-popover-content-available-height)] overflow-y-auto',
          )}>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Ordo UI palette
          </div>
          <div className="grid grid-cols-8 gap-1">
            {COLOR_PAIR_PRESETS.map((preset) => {
              const active = isPresetActive(preset)
              return (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  aria-label={preset.name}
                  aria-pressed={active}
                  title={preset.name}
                  className={cn(
                    'inline-flex h-7 w-7 items-center justify-center rounded-md transition-transform',
                    active ? 'ring-2 ring-offset-1 ring-ring' : 'hover:scale-110',
                  )}
                  style={{ backgroundColor: preset.bg, color: preset.fg }}>
                  <span className="text-[10px] font-bold">A</span>
                </button>
              )
            })}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Background</span>
              <span className="flex items-center gap-2">
                <input
                  type="color"
                  value={swatchHexValue(bgDraft, bg)}
                  onChange={(e) => {
                    // Native color inputs always emit a strict hex.
                    setBgDraft(e.target.value)
                    commitField('bg', e.target.value)
                  }}
                  className="h-8 w-10 cursor-pointer rounded border border-input bg-background"
                  aria-label="Background color"
                />
                <input
                  type="text"
                  value={bgDraft}
                  onChange={(e) => {
                    const value = e.target.value
                    setBgDraft(value)
                    // A fully-typed strict hex commits on the keystroke so it
                    // can never be lost to the popover-unmount race. Case is
                    // preserved here; normalization waits for blur/Enter.
                    if (isCommittableHex(value) && value.trim() !== bg) {
                      commitField('bg', value.trim())
                    }
                  }}
                  onBlur={commitDrafts}
                  onKeyDown={handleHexKeyDown}
                  aria-invalid={bgInvalid || undefined}
                  className={hexInputClass(bgInvalid)}
                  placeholder="#475569"
                />
              </span>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Foreground</span>
              <span className="flex items-center gap-2">
                <input
                  type="color"
                  value={swatchHexValue(fgDraft, fg)}
                  onChange={(e) => {
                    setFgDraft(e.target.value)
                    commitField('fg', e.target.value)
                  }}
                  className="h-8 w-10 cursor-pointer rounded border border-input bg-background"
                  aria-label="Foreground color"
                />
                <input
                  type="text"
                  value={fgDraft}
                  onChange={(e) => {
                    const value = e.target.value
                    setFgDraft(value)
                    if (isCommittableHex(value) && value.trim() !== fg) {
                      commitField('fg', value.trim())
                    }
                  }}
                  onBlur={commitDrafts}
                  onKeyDown={handleHexKeyDown}
                  aria-invalid={fgInvalid || undefined}
                  className={hexInputClass(fgInvalid)}
                  placeholder="#FFFFFF"
                />
              </span>
            </label>
          </div>

          <div className="mt-3 flex items-center gap-3 rounded-md border border-input p-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-md" style={appIconStyle(bg, fg)}>
              <PhosphorIcon
                name={previewIcon}
                className="h-6 w-6"
                fallback={
                  <span className="text-xs font-semibold uppercase">{(previewLabel?.[0] ?? 'A').slice(0, 1)}</span>
                }
              />
            </span>
            <div className="text-xs text-muted-foreground">
              <div className="font-mono text-foreground">{bg.toUpperCase()}</div>
              <div className="font-mono">{fg.toUpperCase()}</div>
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
